// components/PendingInvitesList.tsx
import React, { useCallback, useState } from 'react';
import { Send, Trash2, UserPlus } from '@/components/ui/lucide-memo';
import SimpleLoader from './ui/SimpleLoader';
import { PendingInvite } from '#types/types';

interface PendingInvitesListProps {
  invites: PendingInvite[];
  onResend: (email: string) => Promise<void>;
  onDelete: (inviteId: string) => Promise<void>;
  onCreateDirectly: (email: string) => void;
}

const neutralActionClass =
  'inline-flex h-7 items-center gap-1.5 rounded-md bg-white/[0.04] px-2 text-[11px] text-white/60 transition-colors hover:bg-white/[0.08] hover:text-white/85 disabled:cursor-wait disabled:opacity-40';

const deleteActionClass =
  'inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[11px] text-[#FF7369]/80 transition-colors hover:bg-[#FF7369]/10 hover:text-[#FF7369] disabled:cursor-wait disabled:opacity-40';

const formatDate = (timestamp: number | undefined | null): string => {
  if (typeof timestamp !== 'number' || Number.isNaN(timestamp)) {
    console.warn('formatDate received an invalid timestamp:', timestamp);
    return 'Date unavailable';
  }
  try {
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Date unavailable';
  }
};

interface PendingInviteRowProps {
  invite: PendingInvite;
  isResending: boolean;
  isDeleting: boolean;
  anyResending: boolean;
  anyDeleting: boolean;
  onResend: (email: string) => void;
  onDelete: (inviteId: string) => void;
  onCreateDirectly: (email: string) => void;
}

const PendingInviteRow: React.FC<PendingInviteRowProps> = React.memo(({
  invite,
  isResending,
  isDeleting,
  anyResending,
  anyDeleting,
  onResend,
  onDelete,
  onCreateDirectly,
}) => {
  const handleCreateDirectly = useCallback(() => {
    onCreateDirectly(invite.emailAddress);
  }, [onCreateDirectly, invite.emailAddress]);

  const handleResend = useCallback(() => {
    onResend(invite.emailAddress);
  }, [onResend, invite.emailAddress]);

  const handleDelete = useCallback(() => {
    onDelete(invite.id);
  }, [onDelete, invite.id]);

  return (
    <li className="flex min-h-[52px] flex-wrap items-center justify-between gap-2 rounded-md px-1.5 py-2 transition-colors hover:bg-white/[0.035]">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-white/90" title={invite.emailAddress}>
          {invite.emailAddress}
        </p>
        <p className="text-[11px] text-white/40">Invited {formatDate(invite.createdAt)}</p>
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
        <button
          type="button"
          onClick={handleCreateDirectly}
          className={neutralActionClass}
          disabled={anyResending || anyDeleting}
          title={`Create a user for ${invite.emailAddress}`}
        >
          <UserPlus size={12} />
          <span>Create user</span>
        </button>

        <button
          type="button"
          onClick={handleResend}
          className={neutralActionClass}
          disabled={isResending || anyDeleting}
          title={`Resend the invitation to ${invite.emailAddress}`}
        >
          {isResending ? <SimpleLoader size={12} message="" /> : <Send size={12} />}
          <span>{isResending ? 'Sending...' : 'Resend'}</span>
        </button>

        <button
          type="button"
          onClick={handleDelete}
          className={deleteActionClass}
          disabled={isDeleting || anyResending}
          title={`Delete the invitation for ${invite.emailAddress}`}
        >
          {isDeleting ? <SimpleLoader size={12} message="" /> : <Trash2 size={12} />}
          <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
        </button>
      </div>
    </li>
  );
});

PendingInviteRow.displayName = 'PendingInviteRow';

const PendingInvitesList: React.FC<PendingInvitesListProps> = React.memo(({
  invites,
  onResend,
  onDelete,
  onCreateDirectly,
}) => {
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const [deletingInviteId, setDeletingInviteId] = useState<string | null>(null);

  const handleResendClick = useCallback(async (email: string) => {
    setResendingEmail(email);
    try {
      await onResend(email);
    } finally {
      setResendingEmail(null);
    }
  }, [onResend]);

  const handleDeleteClick = useCallback(async (inviteId: string) => {
    setDeletingInviteId(inviteId);
    try {
      await onDelete(inviteId);
    } finally {
      setDeletingInviteId(null);
    }
  }, [onDelete]);

  if (!invites || invites.length === 0) {
    return <p className="py-5 text-sm text-white/40">No pending invitations.</p>;
  }

  return (
    <ul className="divide-y divide-white/[0.055]">
      {invites.map((invite) => (
        <PendingInviteRow
          key={invite.id}
          invite={invite}
          isResending={resendingEmail === invite.emailAddress}
          isDeleting={deletingInviteId === invite.id}
          anyResending={Boolean(resendingEmail)}
          anyDeleting={Boolean(deletingInviteId)}
          onResend={handleResendClick}
          onDelete={handleDeleteClick}
          onCreateDirectly={onCreateDirectly}
        />
      ))}
    </ul>
  );
});

PendingInvitesList.displayName = 'PendingInvitesList';

export default PendingInvitesList;
